import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch, useApp } from "../../context/AppContext";
import useComplianceLifecycle from "../../hooks/useComplianceLifecycle";
import { Button, Modal, Pagination } from "../../components/common/UI";
import LoadProgress from "../../components/common/LoadProgress";
import PolicyUploadModal from "../../components/common/PolicyUploadModal";
import {
  getDpdpPolicies,
  policiesToPdfSections,
  DPDP_POLICY_COUNT,
} from "../../data/dpdpPolicies";
import {
  exportSubmissionPdf,
  generateSubmissionPdfBlob,
  safeExportFilename,
} from "../../utils/exportSubmissionPdf";
import { confirmAction } from "../../utils/sweetAlert";
import {
  BookOpen,
  Download,
  Eye,
  FileEdit,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";

const cardShell =
  "relative bg-white/60 backdrop-blur-xl rounded-2xl border border-white/50 shadow-lg ring-1 ring-slate-900/[0.04] overflow-hidden transition-all duration-300 hover:shadow-xl hover:ring-slate-900/[0.06] hover:-translate-y-0.5 group";

const UPLOADED_PAGE_SIZE = 8;
const TEMPLATE_PAGE_SIZE = 8;
const MAX_PREVIEW_CACHE = 24;
const policyPreviewCache = new Map();
const previewNonceByPolicyId = new Map();

function getPreviewNonce(policyId) {
  return previewNonceByPolicyId.get(String(policyId || "")) || 0;
}

function bumpPreviewNonce(policyId) {
  if (!policyId) return 0;
  const next = Date.now();
  previewNonceByPolicyId.set(String(policyId), next);
  return next;
}

function getPreviewCacheKey(variant, policy, templatePolicy, organizationName) {
  if (variant === "template" && templatePolicy) {
    return `template:${templatePolicy.id}:${organizationName}`;
  }
  const nonce = getPreviewNonce(policy?.id);
  return `stored:${policy?.id}:${policy?.file_size_bytes ?? 0}:${policy?.updated_at ?? ""}:${nonce}`;
}

function readPreviewCache(key) {
  if (!policyPreviewCache.has(key)) return null;
  const url = policyPreviewCache.get(key);
  policyPreviewCache.delete(key);
  policyPreviewCache.set(key, url);
  return url;
}

function writePreviewCache(key, url) {
  if (!url) return;
  if (policyPreviewCache.has(key)) {
    const existing = policyPreviewCache.get(key);
    policyPreviewCache.delete(key);
    if (existing && existing !== url) URL.revokeObjectURL(existing);
  }
  policyPreviewCache.set(key, url);
  while (policyPreviewCache.size > MAX_PREVIEW_CACHE) {
    const oldest = policyPreviewCache.keys().next().value;
    const oldUrl = policyPreviewCache.get(oldest);
    if (oldUrl && oldUrl !== url) URL.revokeObjectURL(oldUrl);
    policyPreviewCache.delete(oldest);
  }
}

function invalidateStoredPreviewCache(policyId) {
  if (!policyId) return;
  bumpPreviewNonce(policyId);
  const prefixes = [`stored:${policyId}:`, `stored-default:${policyId}:`];
  for (const key of [...policyPreviewCache.keys()]) {
    if (prefixes.some((prefix) => key.startsWith(prefix))) {
      const url = policyPreviewCache.get(key);
      if (url) URL.revokeObjectURL(url);
      policyPreviewCache.delete(key);
    }
  }
}

function createPdfObjectUrl(blob) {
  if (!blob || blob.size === 0) {
    throw new Error("Could not load preview");
  }
  const type = (blob.type || "").split(";")[0].trim().toLowerCase();
  if (
    type.includes("json") ||
    type.includes("html") ||
    type.startsWith("text/")
  ) {
    throw new Error("Could not load preview");
  }
  const pdfBlob =
    type === "application/pdf"
      ? blob
      : new Blob([blob], { type: "application/pdf" });
  return URL.createObjectURL(pdfBlob);
}

async function fetchStoredPolicyObjectUrl(policy) {
  const policyId = policy?.id || policy;
  const token = encodeURIComponent(
    String(
      policy?.file_revision ||
        getPreviewNonce(policyId) ||
        policy?.updated_at ||
        policy?.file_size_bytes ||
        Date.now(),
    ),
  );
  const res = await apiFetch(
    `/policy-store/${policyId}/files/${token}?inline=1`,
    { cache: "reload" },
  );
  if (!res.ok) throw new Error("Could not load preview");
  return createPdfObjectUrl(await res.blob());
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function PolicyPdfPreview({
  previewUrl,
  loading,
  error,
  title,
  interactive = false,
}) {
  if (loading) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 gap-2 px-5">
        <Loader2 size={20} className="animate-spin text-indigo-500" />
        <LoadProgress active label="Loading preview" durationMs={10000} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 gap-1.5 px-4 text-center">
        <FileText size={22} className="text-slate-300" />
        <span className="text-[10px] text-slate-500">{error}</span>
      </div>
    );
  }

  if (previewUrl) {
    // Chrome's PDF viewer breaks on blob URLs that include a hash fragment
    // (broken-document icon). Keep viewer params only for http(s) sources.
    const src = previewUrl.startsWith("blob:")
      ? previewUrl
      : `${previewUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=page-width`;
    return (
      <iframe
        key={src}
        title={`${title} preview`}
        src={src}
        className={`absolute inset-0 w-full h-full border-0 bg-white ${
          interactive ? "" : "pointer-events-none"
        }`}
      />
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50">
      <FileText size={22} className="text-slate-300" />
    </div>
  );
}

function PolicyPreviewModal({
  open,
  policy,
  templatePolicy,
  organizationName,
  initialPreviewUrl,
  onClose,
  onDownload,
  downloading,
  onEditAsWord,
  converting,
  variant = "stored",
}) {
  const [previewUrl, setPreviewUrl] = useState(initialPreviewUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !policy) return undefined;

    const cacheKey = getPreviewCacheKey(
      variant,
      policy,
      templatePolicy,
      organizationName,
    );

    if (variant === "template" && initialPreviewUrl) {
      setPreviewUrl(initialPreviewUrl);
      setLoading(false);
      setError("");
      return undefined;
    }

    // Stored policies must always hit the server after a replace. The
    // in-memory blob cache is only safe for generated templates.
    if (variant === "template") {
      const cachedUrl = readPreviewCache(cacheKey);
      if (cachedUrl) {
        setPreviewUrl(cachedUrl);
        setLoading(false);
        setError("");
        return undefined;
      }
    }

    let cancelled = false;

    setPreviewUrl(null);
    setLoading(true);
    setError("");

    (async () => {
      try {
        let objectUrl = null;
        if (variant === "template" && templatePolicy) {
          const blob = await generateSubmissionPdfBlob({
            title: templatePolicy.title,
            subtitle: templatePolicy.subtitle,
            module: "DPDP Policy",
            organization: organizationName,
            sections: policiesToPdfSections([templatePolicy]),
          });
          if (cancelled) return;
          objectUrl = createPdfObjectUrl(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
        } else {
          objectUrl = await fetchStoredPolicyObjectUrl(policy);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
        }
        if (variant === "template") {
          writePreviewCache(cacheKey, objectUrl);
        }
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (!cancelled) setError(err.message || "Preview unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    policy?.id,
    policy?.updated_at,
    policy?.file_revision,
    policy?.file_size_bytes,
    variant,
    organizationName,
    templatePolicy,
    initialPreviewUrl,
  ]);

  if (!policy) return null;

  const isGenerated = policy.source === "generated";
  const isTemplate = variant === "template";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={policy.title}
      size="xl"
      align="top"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {policy.description && (
              <p className="text-sm text-slate-600 line-clamp-3">
                {policy.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {policy.act_ref && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                  {policy.act_ref}
                </span>
              )}
              {isTemplate ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-indigo-50 text-indigo-700 border-indigo-100">
                  <Sparkles size={10} />
                  Template
                </span>
              ) : (
                <>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                      isGenerated
                        ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                        : "bg-emerald-50 text-emerald-700 border-emerald-100"
                    }`}
                  >
                    {isGenerated ? (
                      <Sparkles size={10} />
                    ) : (
                      <ShieldCheck size={10} />
                    )}
                    {isGenerated ? "AI generated" : "Uploaded"}
                  </span>
                  {policy.file_size_bytes > 0 && (
                    <span className="text-[10px] text-slate-400 tabular-nums">
                      {formatBytes(policy.file_size_bytes)}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="relative w-full h-[min(70vh,720px)] rounded-xl border border-slate-200 bg-slate-100 overflow-hidden shadow-inner">
          <PolicyPdfPreview
            previewUrl={previewUrl}
            loading={loading}
            error={error}
            title={policy.title}
            interactive
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-200">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {onEditAsWord && (
            <Button
              variant="secondary"
              onClick={() => onEditAsWord(policy)}
              disabled={converting}
              className="flex items-center gap-1.5"
            >
              {converting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileEdit size={14} />
              )}
              Edit as Word
            </Button>
          )}
          <Button
            onClick={() => onDownload(policy)}
            disabled={downloading}
            className="flex items-center gap-1.5"
          >
            {downloading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Download PDF
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TemplatePolicyCard({
  policy,
  organizationName,
  storedDocument,
  onPreview,
  onDownload,
  onEditAsWord,
  onReplace,
  downloadingId,
  convertingId,
  loadDelay = 0,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const cacheKey = storedDocument?.id
      ? `stored-default:${storedDocument.id}:${storedDocument.file_revision || ""}`
      : getPreviewCacheKey("template", null, policy, organizationName);

    const buildClientPreview = async () => {
      const blob = await generateSubmissionPdfBlob({
        title: policy.title,
        subtitle: policy.subtitle,
        module: "DPDP Policy",
        organization: organizationName,
        sections: policiesToPdfSections([policy]),
      });
      if (cancelled) return;
      const objectUrl = createPdfObjectUrl(blob);
      writePreviewCache(cacheKey, objectUrl);
      setPreviewUrl(objectUrl);
    };

    timer = setTimeout(() => {
      const cached = readPreviewCache(cacheKey);
      if (cached) {
        setPreviewUrl(cached);
        setLoading(false);
        setError("");
        return;
      }

      (async () => {
        setLoading(true);
        setError("");
        try {
          if (storedDocument?.id) {
            try {
              const objectUrl = await fetchStoredPolicyObjectUrl(storedDocument);
              if (cancelled) {
                URL.revokeObjectURL(objectUrl);
                return;
              }
              writePreviewCache(cacheKey, objectUrl);
              setPreviewUrl(objectUrl);
              return;
            } catch {
              // Fall back to the on-page template if the stored sample is missing.
            }
          }
          await buildClientPreview();
        } catch (err) {
          if (!cancelled) setError(err.message || "Preview unavailable");
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, loadDelay);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [policy, organizationName, loadDelay, storedDocument]);

  const isDownloading =
    downloadingId === policy.id || downloadingId === storedDocument?.id;
  const isConverting = Boolean(
    storedDocument?.id && convertingId === storedDocument.id,
  );
  const canEditStored = Boolean(storedDocument?.id);

  const openPreview = () => {
    if (loading) return;
    onPreview?.(policy, previewUrl);
  };

  return (
    <article
      className={`${cardShell} cursor-pointer`}
      onClick={openPreview}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPreview();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Preview ${policy.title}`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent" />
      <div className="relative aspect-[4/3] bg-white border-b border-slate-100/80 overflow-hidden group/preview">
        <PolicyPdfPreview
          previewUrl={previewUrl}
          loading={loading}
          error={error}
          title={policy.title}
        />
        <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 border border-slate-200/80 text-[10px] font-semibold text-indigo-700 shadow-sm">
          <Sparkles size={10} />
          Template
        </div>
        {!loading && !error && previewUrl && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover/preview:bg-slate-900/25 transition-colors">
            <span className="opacity-0 group-hover/preview:opacity-100 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-xs font-semibold text-slate-800 shadow-md transition-opacity">
              <Eye size={14} />
              Preview
            </span>
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2">
            {policy.title}
          </h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-2">
            {policy.subtitle}
          </p>
          <span className="inline-flex mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
            {policy.actRef}
          </span>
        </div>
        <div
          className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-[10px] text-slate-400 truncate">
            Click card to preview
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => canEditStored && onEditAsWord?.(storedDocument)}
              disabled={!canEditStored || isConverting}
              className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-60"
              title={
                canEditStored
                  ? "Edit as Word document"
                  : "Template file is still being prepared"
              }
            >
              {isConverting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileEdit size={14} />
              )}
            </button>
            <button
              type="button"
              onClick={() => canEditStored && onReplace?.(storedDocument)}
              disabled={!canEditStored}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
              title={
                canEditStored
                  ? "Edit details / replace file"
                  : "Template file is still being prepared"
              }
            >
              <FileText size={14} />
            </button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onDownload(policy)}
              disabled={isDownloading}
              className="flex items-center gap-1.5 !text-xs"
            >
              {isDownloading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Download size={13} />
              )}
              Download
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

function UploadedPolicyCard({
  policy,
  onPreview,
  onDownload,
  onDelete,
  onEdit,
  onEditAsWord,
  downloadingId,
  deletingId,
  convertingId,
  previewEpoch = 0,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    setPreviewUrl(null);
    setLoading(true);
    setError("");

    (async () => {
      try {
        const objectUrl = await fetchStoredPolicyObjectUrl(policy);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (!cancelled) setError(err.message || "Preview unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    policy.id,
    policy.file_size_bytes,
    policy.updated_at,
    policy.file_revision,
    previewEpoch,
  ]);

  const isDownloading = downloadingId === policy.id;
  const isDeleting = deletingId === policy.id;
  const isConverting = convertingId === policy.id;
  const isGenerated = policy.source === "generated";

  const openPreview = () => {
    if (loading) return;
    onPreview?.(policy, previewUrl);
  };

  return (
    <article
      className={`${cardShell} cursor-pointer`}
      onClick={openPreview}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPreview();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Preview ${policy.title}`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
      <div className="relative aspect-[4/3] bg-white border-b border-slate-100/80 overflow-hidden group/preview">
        <PolicyPdfPreview
          previewUrl={previewUrl}
          loading={loading}
          error={error}
          title={policy.title}
        />
        <div
          className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 border text-[10px] font-semibold shadow-sm ${
            isGenerated
              ? "border-indigo-200/80 text-indigo-700"
              : "border-slate-200/80 text-emerald-700"
          }`}
        >
          {isGenerated ? <Sparkles size={10} /> : <ShieldCheck size={10} />}
          {isGenerated ? "AI generated" : "Uploaded"}
        </div>
        {!loading && !error && previewUrl && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover/preview:bg-slate-900/25 transition-colors">
            <span className="opacity-0 group-hover/preview:opacity-100 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-xs font-semibold text-slate-800 shadow-md transition-opacity">
              <Eye size={14} />
              Preview
            </span>
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2">
            {policy.title}
          </h3>
          {policy.description && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">
              {policy.description}
            </p>
          )}
          {policy.act_ref && (
            <span className="inline-flex mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
              {policy.act_ref}
            </span>
          )}
        </div>
        <div
          className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-[10px] text-slate-400 truncate">
            Click card to preview
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onEditAsWord && onEditAsWord(policy)}
              disabled={isConverting}
              className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-60"
              title="Edit as Word document"
            >
              {isConverting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileEdit size={14} />
              )}
            </button>
            <button
              type="button"
              onClick={() => onEdit && onEdit(policy)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
              title="Edit details / replace file"
            >
              <FileText size={14} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(policy)}
              disabled={isDeleting}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Delete policy"
            >
              {isDeleting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
            </button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onDownload(policy)}
              disabled={isDownloading}
              className="flex items-center gap-1.5 !text-xs"
            >
              {isDownloading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Download size={13} />
              )}
              Download
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function PolicyStore() {
  const { showNotification } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const { orgName } = useComplianceLifecycle();
  const organizationName = orgName || "your organization";
  const [defaultDocs, setDefaultDocs] = useState({});

  const templatePolicies = useMemo(
    () => getDpdpPolicies({ organizationName }),
    [organizationName],
  );

  const [uploadedPolicies, setUploadedPolicies] = useState([]);
  const [uploadedPage, setUploadedPage] = useState(1);
  const [uploadedTotal, setUploadedTotal] = useState(0);
  const [uploadedPages, setUploadedPages] = useState(1);
  const [templatePage, setTemplatePage] = useState(1);
  const [loadingUploaded, setLoadingUploaded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [previewEpoch, setPreviewEpoch] = useState(0);

  const loadDefaults = useCallback(async () => {
    try {
      const res = await apiFetch("/policy-store/recommendation-index", {
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.defaults) setDefaultDocs(data.defaults);
    } catch {
      // Template cards fall back to the built-in sample until the index loads.
    }
  }, []);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  useEffect(() => {
    const open = location.state?.openPolicy;
    if (!open) return;
    if (open.id) {
      setPreviewTarget({
        type: "stored",
        policy: {
          id: open.id,
          title: open.title || "Policy",
          description: open.description || "",
          act_ref: open.act_ref || "",
          file_revision: open.file_revision,
          source: open.source || "uploaded",
          is_default: Boolean(open.is_default),
        },
      });
    } else if (open.catalogueKey) {
      const template = getDpdpPolicies({ organizationName }).find(
        (item) => item.id === open.catalogueKey,
      );
      if (template) {
        setPreviewTarget({
          type: "template",
          policy: {
            id: template.id,
            title: template.title,
            description: template.subtitle,
            act_ref: template.actRef,
            source: "template",
            file_size_bytes: 0,
          },
          templatePolicy: template,
        });
      }
    }
    navigate("/user/policy-store", { replace: true, state: {} });
  }, [location.state, navigate, organizationName]);

  const loadUploaded = useCallback(
    async (silent = false, page = 1) => {
      if (!silent) setLoadingUploaded(true);
      else setRefreshing(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(UPLOADED_PAGE_SIZE),
        });
        const res = await apiFetch(`/policy-store?${params}`, {
          cache: "no-store",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Failed to load policies");
        setUploadedPolicies(data.items || []);
        setUploadedTotal(data.total ?? 0);
        setUploadedPages(data.pages ?? 1);
        setUploadedPage(data.page ?? page);
      } catch (err) {
        if (!silent)
          showNotification(err.message || "Failed to load policies", "error");
      } finally {
        setLoadingUploaded(false);
        setRefreshing(false);
      }
    },
    [showNotification],
  );

  useEffect(() => {
    loadUploaded(false, uploadedPage);
  }, [uploadedPage, loadUploaded]);

  const templatePages = Math.max(
    1,
    Math.ceil(templatePolicies.length / TEMPLATE_PAGE_SIZE),
  );
  const pagedTemplates = useMemo(() => {
    const start = (templatePage - 1) * TEMPLATE_PAGE_SIZE;
    return templatePolicies.slice(start, start + TEMPLATE_PAGE_SIZE);
  }, [templatePolicies, templatePage]);

  useEffect(() => {
    if (templatePage > templatePages) {
      setTemplatePage(templatePages);
    }
  }, [templatePage, templatePages]);

  const handleDownloadTemplate = async (policy) => {
    setDownloadingId(policy.id);
    try {
      await exportSubmissionPdf({
        title: policy.title,
        subtitle: policy.subtitle,
        module: "DPDP Policy",
        organization: organizationName,
        filename: `DPDP_${safeExportFilename(policy.title)}`,
        sections: policiesToPdfSections([policy]),
      });
    } catch (err) {
      showNotification(err.message || "Download failed", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadUploaded = async (policy) => {
    setDownloadingId(policy.id);
    try {
      const res = await apiFetch(
        `/policy-store/${policy.id}/files/${encodeURIComponent(policy.file_revision || policy.updated_at || Date.now())}?inline=0`,
        { cache: "reload" },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download =
        policy.file_name || `${safeExportFilename(policy.title)}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showNotification(err.message || "Download failed", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const pollEditDocxStatus = async (
    policyId,
    taskId,
    { intervalMs = 1500, timeoutMs = 120000 } = {},
  ) => {
    const startedAt = Date.now();
    for (;;) {
      const res = await apiFetch(
        `/policy-store/${policyId}/edit-docx/status/${taskId}`,
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Could not check conversion status");
      }
      const data = await res.json();
      if (data.status === "success") return data;
      if (data.status === "error") {
        throw new Error(data.message || "Conversion failed");
      }
      if (Date.now() - startedAt > timeoutMs) {
        throw new Error(
          "Conversion is taking longer than expected. Please try again.",
        );
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  };

  const handleEditAsWord = async (policy) => {
    setConvertingId(policy.id);
    try {
      // 1. Kick off the background conversion
      const startRes = await apiFetch(`/policy-store/${policy.id}/edit-docx`, {
        method: "POST",
      });
      if (!startRes.ok) {
        const data = await startRes.json().catch(() => ({}));
        throw new Error(
          data.detail || "Could not start the Word document conversion",
        );
      }
      const { task_id: taskId } = await startRes.json();

      // 2. Poll until the worker finishes
      await pollEditDocxStatus(policy.id, taskId);

      // 3. Download the finished .docx
      const resultRes = await apiFetch(
        `/policy-store/${policy.id}/edit-docx/result/${taskId}`,
      );
      if (!resultRes.ok) {
        const data = await resultRes.json().catch(() => ({}));
        throw new Error(
          data.detail || "Could not download the converted document",
        );
      }
      const blob = await resultRes.blob();
      const url = URL.createObjectURL(blob);
      const baseName = policy.file_name
        ? policy.file_name.replace(/\.pdf$/i, "")
        : safeExportFilename(policy.title);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${baseName}.docx`;
      anchor.click();
      URL.revokeObjectURL(url);
      showNotification(
        "Editable Word document downloaded. Once you're done editing, use “Edit details / replace file” to upload it back.",
        "success",
      );
    } catch (err) {
      showNotification(
        err.message || "Could not prepare an editable Word document",
        "error",
      );
    } finally {
      setConvertingId(null);
    }
  };

  const handleDelete = async (policy) => {
    const ok = await confirmAction({
      title: "Delete policy?",
      text: `Remove "${policy.title}" from the policy store.`,
      confirmText: "Delete",
      icon: "warning",
    });
    if (!ok) return;
    setDeletingId(policy.id);
    try {
      const res = await apiFetch(`/policy-store/${policy.id}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Delete failed");
      invalidateStoredPreviewCache(policy.id);
      showNotification("Policy removed", "success");
      const nextPage =
        uploadedPolicies.length === 1 && uploadedPage > 1
          ? uploadedPage - 1
          : uploadedPage;
      if (nextPage !== uploadedPage) {
        setUploadedPage(nextPage);
      } else {
        await loadUploaded(true, uploadedPage);
      }
    } catch (err) {
      showNotification(err.message || "Delete failed", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const totalCount = uploadedTotal + templatePolicies.length;

  return (
    <div
      className="min-h-full -m-4 lg:-m-6 font-sans"
      style={{
        background:
          "linear-gradient(160deg, #f8fafc 0%, #eff6ff 30%, #eef2ff 55%, #f0f9ff 100%)",
      }}
    >
      <header
        data-tour-id="tour-policy-header"
        className="relative bg-white/70 backdrop-blur-xl border-b border-white/50 px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3 shadow-sm"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #6366f1, #0ea5e9)",
              boxShadow: "0 6px 20px rgba(99,102,241,0.35)",
            }}
          >
            <BookOpen size={18} className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">
              Policy Store
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Central repository for DPDP policies — {totalCount} document
              {totalCount !== 1 ? "s" : ""} for {organizationName}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-tour-id="tour-policy-refresh"
            onClick={() => {
              loadUploaded(true, uploadedPage);
              loadDefaults();
            }}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 bg-white/80 border border-slate-200/80 shadow-sm hover:-translate-y-0.5 transition-all"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
          <Button
            data-tour-id="tour-policy-upload"
            onClick={() => {
              setEditingPolicy(null);
              setUploadOpen(true);
            }}
            className="flex items-center gap-1.5 !rounded-xl shadow-md"
          >
            <Plus size={14} />
            Upload policy
          </Button>
        </div>
      </header>

      {convertingId && (
        <div className="px-4 sm:px-6 pt-3">
          <LoadProgress active label="Preparing Word document" durationMs={90000} />
        </div>
      )}

      <div className="px-4 sm:px-6 py-5 space-y-6">
        {uploadedTotal > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={16} className="text-emerald-600" />
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                Organisation policies
              </h2>
              <span className="text-xs text-slate-500">
                ({uploadedTotal})
              </span>
            </div>
            {loadingUploaded && uploadedPolicies.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-slate-500 text-sm gap-2">
                <Loader2 size={16} className="animate-spin" />
                Loading uploaded policies…
              </div>
            ) : (
              <>
            <div
              data-tour-id="tour-policy-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              {uploadedPolicies.map((policy) => (
                <UploadedPolicyCard
                  key={`${policy.id}:${policy.file_revision || policy.updated_at || ""}:${previewEpoch}`}
                  policy={policy}
                  previewEpoch={previewEpoch}
                  onPreview={(p, url) =>
                    setPreviewTarget({
                      type: "stored",
                      policy: p,
                      previewUrl: url,
                    })
                  }
                  onDownload={handleDownloadUploaded}
                  onDelete={handleDelete}
                  onEdit={(p) => {
                    setUploadOpen(true);
                    setEditingPolicy(p);
                  }}
                  onEditAsWord={handleEditAsWord}
                  downloadingId={downloadingId}
                  deletingId={deletingId}
                  convertingId={convertingId}
                />
              ))}
            </div>
            {uploadedTotal > UPLOADED_PAGE_SIZE && (
              <div className="mt-4 px-1">
                <Pagination
                  page={uploadedPage}
                  pages={uploadedPages}
                  total={uploadedTotal}
                  pageSize={UPLOADED_PAGE_SIZE}
                  onPageChange={setUploadedPage}
                  itemLabel="policies"
                />
              </div>
            )}
              </>
            )}
          </section>
        )}

        {loadingUploaded && uploadedTotal === 0 && (
          <div className="flex items-center justify-center py-8 text-slate-500 text-sm gap-2">
            <Loader2 size={16} className="animate-spin" />
            Loading uploaded policies…
          </div>
        )}

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-indigo-600" />
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
              DPDP policy templates
            </h2>
            <span className="text-xs text-slate-500">
              ({DPDP_POLICY_COUNT})
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4 max-w-2xl">
            Ready-to-use policy templates aligned with the Digital Personal Data
            Protection Act, 2023. Edit or replace a template here. A generated
            or uploaded policy for the same topic still takes priority.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {pagedTemplates.map((policy, index) => (
              <TemplatePolicyCard
                key={policy.id}
                policy={policy}
                organizationName={organizationName}
                storedDocument={defaultDocs[policy.id]}
                onPreview={(p, url) =>
                  setPreviewTarget({
                    type: "template",
                    policy: {
                      id: defaultDocs[p.id]?.id || p.id,
                      title: p.title,
                      description: p.subtitle,
                      act_ref: p.actRef,
                      source: "template",
                      file_size_bytes: defaultDocs[p.id]?.file_revision ? 1 : 0,
                      file_revision: defaultDocs[p.id]?.file_revision,
                      is_default: true,
                    },
                    previewUrl: url,
                    templatePolicy: p,
                    storedDocument: defaultDocs[p.id] || null,
                  })
                }
                onDownload={(p) => {
                  const stored = defaultDocs[p.id];
                  if (stored?.id) handleDownloadUploaded(stored);
                  else handleDownloadTemplate(p);
                }}
                onEditAsWord={handleEditAsWord}
                onReplace={(stored) => {
                  setEditingPolicy({
                    ...stored,
                    origin: "default",
                    catalogue_key: stored.catalogue_key || policy.id,
                  });
                  setUploadOpen(true);
                }}
                downloadingId={downloadingId}
                convertingId={convertingId}
                loadDelay={index * 350}
              />
            ))}
          </div>
          {templatePolicies.length > TEMPLATE_PAGE_SIZE && (
            <div className="mt-4 px-1">
              <Pagination
                page={templatePage}
                pages={templatePages}
                total={templatePolicies.length}
                pageSize={TEMPLATE_PAGE_SIZE}
                onPageChange={setTemplatePage}
                itemLabel="templates"
              />
            </div>
          )}
        </section>
      </div>

      <PolicyPreviewModal
        key={
          previewTarget
            ? `${previewTarget.policy?.id}:${previewTarget.policy?.file_revision || previewTarget.policy?.updated_at || ""}:${getPreviewNonce(previewTarget.policy?.id)}:${previewEpoch}`
            : "closed"
        }
        open={Boolean(previewTarget)}
        policy={previewTarget?.policy}
        templatePolicy={previewTarget?.templatePolicy}
        organizationName={organizationName}
        initialPreviewUrl={previewTarget?.previewUrl}
        variant={previewTarget?.type === "template" ? "template" : "stored"}
        onClose={() => setPreviewTarget(null)}
        onDownload={(policy) => {
          const stored = previewTarget?.storedDocument;
          if (stored?.id) {
            handleDownloadUploaded({
              ...stored,
              file_name: stored.file_name,
            });
            return;
          }
          if (
            previewTarget?.type === "template" &&
            previewTarget.templatePolicy
          ) {
            handleDownloadTemplate(previewTarget.templatePolicy);
          } else {
            handleDownloadUploaded(policy);
          }
        }}
        downloading={
          downloadingId ===
          (previewTarget?.storedDocument?.id ||
            previewTarget?.policy?.id ||
            previewTarget?.templatePolicy?.id)
        }
        onEditAsWord={
          (
            previewTarget?.type === "template"
              ? previewTarget?.storedDocument?.id ||
                defaultDocs[previewTarget?.templatePolicy?.id]?.id
              : previewTarget?.policy?.id
          )
            ? () => {
                const stored =
                  previewTarget?.type === "template"
                    ? previewTarget.storedDocument ||
                      defaultDocs[previewTarget?.templatePolicy?.id]
                    : previewTarget.policy;
                if (stored?.id) handleEditAsWord(stored);
              }
            : undefined
        }
        converting={
          convertingId ===
          (previewTarget?.type === "template"
            ? previewTarget?.storedDocument?.id ||
              defaultDocs[previewTarget?.templatePolicy?.id]?.id
            : previewTarget?.policy?.id)
        }
      />

      <PolicyUploadModal
        open={uploadOpen}
        policy={editingPolicy}
        onClose={() => {
          setUploadOpen(false);
          setEditingPolicy(null);
        }}
        onSuccess={(policy) => {
          const replacedId = editingPolicy?.id;
          const replacedDefault =
            editingPolicy?.origin === "default" || policy?.origin === "default";
          if (replacedId && replacedDefault) {
            invalidateStoredPreviewCache(replacedId);
            const catalogueKey =
              policy?.catalogue_key || editingPolicy?.catalogue_key;
            if (catalogueKey && policy?.id) {
              setDefaultDocs((prev) => ({
                ...prev,
                [catalogueKey]: {
                  ...(prev[catalogueKey] || {}),
                  ...policy,
                  is_default: true,
                },
              }));
            }
            loadDefaults();
            setPreviewEpoch((n) => n + 1);
            setPreviewTarget(null);
            setEditingPolicy(null);
          } else if (replacedId) {
            invalidateStoredPreviewCache(replacedId);
            setPreviewEpoch((n) => n + 1);
            setPreviewTarget(null);
            if (policy?.id) {
              setUploadedPolicies((prev) =>
                prev.map((item) =>
                  item.id === policy.id ? { ...item, ...policy } : item,
                ),
              );
            }
            loadUploaded(true, uploadedPage);
            setEditingPolicy(null);
          } else if (policy?.id) {
            setUploadedPage(1);
            loadUploaded(true, 1);
          } else {
            loadUploaded(true, uploadedPage);
          }
          setUploadOpen(false);
        }}
      />
    </div>
  );
}
